import { lazy, Suspense } from 'react';
import clsx from 'clsx';
import { extractHeadings, type DocPageWithContent } from '@/services/docs';
import DocsToc from './DocsToc';

// Lazy-load the markdown renderer so the react-markdown + remark/rehype stack
// (~180 kB) ships in its own chunk fetched only when an article actually
// renders. Keeping this import static would pull the whole stack into the
// eager route-tree graph via the co-located DocsGroupPageLoading skeleton
// (used as a pendingComponent), which would modulepreload it for every visitor.
const DocsArticle = lazy(() => import('./DocsArticle'));

interface DocsGroupPageProps {
  page: DocPageWithContent | undefined;
}

/** Shared article page for every group: header, body, and table of contents. */
export function DocsGroupPage({ page }: DocsGroupPageProps) {
  if (!page) {
    return (
      <div className="flex items-center justify-center p-20">
        <div className="text-center">
          <h1 className="text-copy-primary mb-2 text-2xl font-bold">Page Not Found</h1>
          <p className="text-copy-secondary">
            The documentation page you're looking for doesn't exist.
          </p>
        </div>
      </div>
    );
  }

  const headings = extractHeadings(page.content);

  return (
    <div className="docs-page-layout">
      <div className="docs-page-content">
        <header className="docs-page-header">
          <h1 className="docs-page-title">{page.title}</h1>
          {page.description && (
            <p className="docs-page-description">{page.description}</p>
          )}
        </header>
        <Suspense fallback={<DocsArticleFallback />}>
          <DocsArticle content={page.content} />
        </Suspense>
      </div>
      <DocsToc key={`${page.group}/${page.section}/${page.slug}`} headings={headings} />
    </div>
  );
}

/** A single placeholder line tinted to match the sidebar nav (`--bg-rail`). */
function FallbackLine({ className }: { className?: string }) {
  return <div className={clsx('h-4 rounded bg-[var(--bg-rail)]', className)} />;
}

/**
 * Body placeholder shown while the lazy markdown chunk loads. Mimics the shape
 * of a full article — intro paragraph, a couple of sections with headings,
 * a bulleted list, and a code block — so the layout doesn't jump when the real
 * content arrives.
 */
function DocsArticleFallback() {
  return (
    <div className="animate-pulse space-y-8" aria-hidden="true">
      {/* Intro paragraph */}
      <div className="space-y-3">
        <FallbackLine className="w-full" />
        <FallbackLine className="w-11/12" />
        <FallbackLine className="w-full" />
        <FallbackLine className="w-3/4" />
      </div>

      {/* Section: heading + paragraph */}
      <div className="space-y-4">
        <FallbackLine className="h-6 w-2/5" />
        <div className="space-y-3">
          <FallbackLine className="w-full" />
          <FallbackLine className="w-10/12" />
          <FallbackLine className="w-11/12" />
        </div>
      </div>

      {/* Section: heading + bulleted list */}
      <div className="space-y-4">
        <FallbackLine className="h-6 w-1/3" />
        <div className="space-y-3 pl-5">
          <FallbackLine className="w-9/12" />
          <FallbackLine className="w-7/12" />
          <FallbackLine className="w-8/12" />
        </div>
      </div>

      {/* Code block */}
      <div className="h-28 w-full rounded-lg bg-[var(--bg-rail)]" />

      {/* Closing paragraph */}
      <div className="space-y-3">
        <FallbackLine className="w-full" />
        <FallbackLine className="w-11/12" />
        <FallbackLine className="w-2/3" />
      </div>
    </div>
  );
}

export function DocsGroupPageLoading() {
  return (
    <div className="docs-page-layout">
      <div className="docs-page-content">
        <header className="docs-page-header">
          <div className="bg-surface-secondary h-10 w-1/2 animate-pulse rounded" />
          <div className="bg-surface-secondary mt-4 h-6 w-2/3 animate-pulse rounded" />
        </header>
      </div>
    </div>
  );
}

export default DocsGroupPage;
